/** @format */

import React, { FC, useEffect, useState } from 'react'
import styled from 'styled-components'
import { PrometheusAlarmTabProps } from 'src/pages/PrometheusMonitor/Alarm'
import {
  _$,
  Button,
  Select,
  Option,
  Table,
  TableColumn,
  Pagination,
  Dialog,
  Message,
  Icon,
  Ellipsis
} from '@king-design/react'
import { useHistory, useLocation } from 'react-router-dom'
import { PageProps, initPage } from 'src/utils/interface'
import { useAsyncFn } from 'react-use'
import {
  getPrometheusAlertInhibitions,
  deletePrometheusAlertInhibition
} from 'src/api/ApiPrometheusAlarm'
import { MonitorSearch, TableSearch, parseTableSearchParams } from 'src/components'

interface SearchProps extends PageProps {
  InstanceID: string
  Name: string
}

const initSearch: SearchProps = {
  InstanceID: '',
  Name: '',
  ...initPage
}

const SilenceTab: FC<PrometheusAlarmTabProps> = ({ className, instances, region }) => {
  const history = useHistory()
  const location = useLocation()
  const [search, setSearch] = useState<SearchProps>(
    () => parseTableSearchParams<SearchProps>(location.search) || initSearch
  )

  useEffect(() => {
    ruleFetch()
  }, [search, region])

  const [{ value: rules }, ruleFetch] = useAsyncFn(
    async (values?) => {
      const params = {
        ...search,
        ...values
      }
      const result = await getPrometheusAlertInhibitions(params)
      return {
        data: result?.Rules || [],
        total: result?.TotalCount || 0
      }
    },
    [search]
  )

  // 删除规则
  const [, deleteFetch] = useAsyncFn(
    async ID => {
      const result = await deletePrometheusAlertInhibition({
        ID
      })
      if (result?.code === 'Success') {
        Message.success(_$('删除成功'))
        setSearch({
          ...search,
          Page: 1
        })
      } else {
        result?.message && Message.error(result?.message)
      }
    },
    [search]
  )

  return (
    <div className={className}>
      <TableSearch params={search} syncUrl={false}>
        <div className="search_item">
          <Select
            value={search.InstanceID}
            clearable
            filterable
            placeholder={_$('请选择关联资源')}
            slotPrefix={`${_$('关联资源')}：`}
            onChangeValue={value => {
              setSearch({
                ...search,
                InstanceID: value,
                Page: 1
              })
            }}
          >
            {instances?.map(item => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="search_item">
          <MonitorSearch
            placeholder={_$('请输入抑制规则，支持模糊搜索')}
            prefix={`${_$('抑制规则')}：`}
            value={search.Name}
            onChange={value => {
              setSearch({
                ...search,
                Page: 1,
                Name: value
              })
            }}
          />
        </div>
      </TableSearch>
      <div className="header">
        <Button
          type="primary"
          onClick={() => {
            history.push('/monitor/prometheusmonitor/alarm/inhibition/edit')
          }}
        >
          <Icon className="ion-plus-round" />
          {_$('新建抑制规则')}
        </Button>
      </div>
      <Table
        stickHeader={50}
        data={rules?.data || []}
        rowKey={data => data.ID}
        checkType="none"
        animation={false}
      >
        <TableColumn key="Name" title={_$('抑制规则名称')} />
        <TableColumn
          key="BindInstances"
          title={_$('关联资源')}
          slotTemplate={([data]) => {
            return data.BindInstances?.length
              ? data.BindInstances?.map(item => {
                  return (
                    <>
                      <p>{item.InstanceName}</p>
                      <Ellipsis className="table_text" maxLines={2} hoverable>
                        {item.InstanceID}
                      </Ellipsis>
                    </>
                  )
                })
              : '-'
          }}
        ></TableColumn>
        <TableColumn
          key="Equals"
          title={_$('匹配条件')}
          slotTemplate={([{ Equals }]) => {
            return Equals?.join('、') || '-'
          }}
        ></TableColumn>
        <TableColumn
          key="Description"
          title={_$('描述')}
          slotTemplate={([{ Description }]) => {
            return Description ? (
              <Ellipsis maxLines={3} hoverable>
                {Description}
              </Ellipsis>
            ) : (
              '-'
            )
          }}
        ></TableColumn>
        <TableColumn
          key="UpdatedAt"
          title={_$('编辑时间')}
          slotTemplate={([{ UpdatedAt }]) => {
            return UpdatedAt || '-'
          }}
        ></TableColumn>
        <TableColumn
          key="operate"
          title={_$('操作')}
          width="150"
          slotTemplate={([record]) => {
            return (
              <div className="ksc-space">
                <a
                  onClick={() => {
                    history.push(
                      `/monitor/prometheusmonitor/alarm/inhibition/edit?inhibitionID=${record.ID}`
                    )
                  }}
                >
                  {_$('编辑')}
                </a>
                <a
                  onClick={() => {
                    history.push(
                      `/monitor/prometheusmonitor/alarm/inhibition/edit?copyID=${record.ID}`
                    )
                  }}
                >
                  {_$('复制')}
                </a>
                <a
                  onClick={() => {
                    let dialog: Dialog
                    Dialog.confirm({
                      content: _$('是否确认删除【{name}】抑制规则？', { name: record?.Name }),
                      title: record?.Name,
                      ref: _dialog => (dialog = _dialog),
                      okText: _$('确认删除'),
                      ok: async () => {
                        dialog.showLoading()
                        await deleteFetch(record?.ID)
                        dialog.close()
                      }
                    })
                  }}
                >
                  {_$('删除')}
                </a>
              </div>
            )
          }}
        ></TableColumn>
      </Table>
      {rules !== undefined ? (
        <Pagination
          value={search.Page}
          limit={search.PageSize}
          total={rules?.total || 0}
          size="small"
          onChangeValue={value => {
            setSearch(state => {
              return {
                ...state,
                Page: value
              }
            })
          }}
          onChangeLimit={limit => {
            setSearch({
              ...search,
              PageSize: limit
            })
          }}
        />
      ) : null}
    </div>
  )
}

export default styled(SilenceTab)`
  margin-top: 16px;
`
